<div class="profile-modal">

    <input type="file" (change)="onFileSelected($event)" hidden #fileInput />

    <header class="profile-modal__hero">
        <button
                type="button"
                class="profile-modal__close"
                aria-label="Close profile"
                mat-dialog-close
        >
            <app-icon icon="xmark"></app-icon>
        </button>

        @if (!uploadingAvatar()) {

            <div class="profile-modal__avatar-wrap">
                <button
                        type="button"
                        class="profile-modal__avatar-button"
                        aria-label="Change profile photo"
                        matTooltip="Change Profile Photo"
                        matTooltipPosition="below"
                        (click)="fileInput.click()"
                >
                    <img
                            class="profile-modal__avatar"
                            [src]="avatarUrl()"
                            [alt]="username() + ' profile photo'"
                    />
                </button>
            </div>

        }
    </header>

    @if (uploadingAvatar()) {

        <main class="profile-modal__body">

            <h3 class="profile-modal__avatar-editing">
                Create Your Profile Picture
            </h3>

            <div class="profile-avatar-uploader">
                <image-cropper
                        [imageChangedEvent]="fileChangeEvent"
                        [maintainAspectRatio]="true"
                        format="png"
                        [roundCropper]="true"
                        (imageCropped)="imageCropped($event)"
                >

                </image-cropper>
            </div>
        </main>

        <section class="profile-modal__account">

            <div class="profile-modal__actions">
                <button
                        type="button"
                        matButton="elevated"
                        (click)="cancelAvatarUpload()"
                >
                    <span>Cancel</span>
                </button>

                <button
                        type="button"
                        class="profile-modal__pill-button"
                        (click)="saveAvatarUpload()"
                >
                    <span>Save</span>
                </button>

            </div>

        </section>

    } @else {

        <main class="profile-modal__body">
            <h2 class="profile-modal__name">
                {{ fullName() }}
            </h2>

            <div class="profile-modal__handle">
                {{ displayUsername() }}
            </div>

            <section class="profile-modal__details" aria-label="Profile details">
                <div class="profile-modal__detail-row">
                    <app-icon icon="envelope"></app-icon>

                    <div class="profile-modal__detail-content">
                        <div class="profile-modal__detail-label">Email</div>
                        <div class="profile-modal__detail-value">
                            {{ email() }}
                        </div>
                    </div>
                </div>

                <div class="profile-modal__detail-row">
                    <app-icon icon="building"></app-icon>

                    <div class="profile-modal__detail-content">
                        <div class="profile-modal__detail-label">Company</div>
                        <div class="profile-modal__detail-value">
                            {{ organizationName() }}
                        </div>
                    </div>
                </div>

                @for (login of activeSocialLogins(); track login.id) {
                    <div class="profile-modal__detail-row" [class.profile-modal__detail-row--social]="login.editable">
                        <app-icon [icon]="login.icon"></app-icon>

                        <div class="profile-modal__detail-content">
                            <div class="profile-modal__detail-label">{{ login.label }}</div>
                            <div class="profile-modal__detail-value">{{ login.email }}</div>
                        </div>

                        @if (login.editable) {
                            <button
                                    type="button"
                                    class="profile-modal__unlink-button"
                                    aria-label="Unlink {{ login.label }}"
                                    (click)="disconnectSocialLogin(login.id)"
                            >
                                <app-icon icon="xmark"></app-icon>
                            </button>
                        }
                    </div>
                }

            </section>
        </main>

        @if (isImpersonating()) {
            <section class="profile-modal__account">
                <h3>Impersonated by {{ displayImpersonator() }}</h3>

                <div class="profile-modal__actions">
                    <button
                            type="button"
                            class="profile-modal__pill-button"
                            [disabled]="profileBusy()"
                            (click)="profileEndImpersonation()"
                    >
                        <app-icon icon="person-exit"></app-icon>
                        <span>Exit Impersonation</span>
                    </button>
                </div>
            </section>
        } @else {
            <section class="profile-modal__account">
                <h3>Manage Account</h3>

                <div class="profile-modal__actions">
                    <button
                            type="button"
                            class="profile-modal__pill-button"
                            [disabled]="profileBusy()"
                            (click)="addPassKey()"
                            matTooltip="Add a passkey to your current browser so you can log in with a passwordless authentication method"
                    >
                        <app-icon icon="key"></app-icon>
                        <span>Add Passkey</span>
                    </button>

                    @for (socialLogin of inactiveSocialLogins(); track socialLogin.id) {
                        <button
                                type="button"
                                class="profile-modal__pill-button"
                                (click)="connectSocialLogin(socialLogin.id)"
                                matTooltip="Connect your account to another provider so you can log in with their credentials in the future"
                        >
                            <app-icon [icon]="socialLogin.icon"></app-icon>
                            <span>Connect {{ socialLogin.label }}</span>
                        </button>
                    }

                    @if (showImpersonationMenu()) {
                        <button
                                [matMenuTriggerFor]="impersonationMenu"
                                [disabled]="profileBusy()"
                                class="profile-modal__pill-button"
                        >
                            <app-icon icon="person-switch"></app-icon>
                            <span>Impersonate</span>
                        </button>
                    }
                </div>

                <mat-menu #impersonationMenu>
                    @for (username of profileImpersonationMenu(); track username) {
                        <button mat-menu-item (click)="profileImpersonate(username)">
                            <span>{{ username }}</span>
                        </button>
                    }
                </mat-menu>


                <button
                        matButton="elevated"
                        type="button"
                        class="profile-modal__logout"
                        [disabled]="profileBusy()"
                        (click)="logout()"
                >
                    Log Out
                </button>
            </section>
        }
    }

    <footer class="profile-modal__footer">
        <div class="profile-modal__legal">
            @if (privacyPolicyUrl() !== null) {
                <a [href]="privacyPolicyUrl()">Privacy Policy</a>
            }
            @if (privacyPolicyUrl() !== null && generalTermsUrl() !== null) {
                <span aria-hidden="true">•</span>
            }
            @if (generalTermsUrl() !== null) {
                <a [href]="generalTermsUrl()">Terms of Service</a>
            }
        </div>

        <div class="profile-modal__branding">
            <div class="profile-modal__brand-item">
                <button matIconButton [disabled]="profileBusy()" (click)="profileRefresh()">
                    <app-icon [spin]="profileBusy()" icon="refresh"></app-icon>
                </button>
            </div>

            <div class="profile-modal__brand-item">
                <img [src]="identityLogo().logoUrl" alt="" />
                <span>{{ identityLogo().identityName }}</span>
            </div>
        </div>
    </footer>

</div>